import { useState, useEffect } from 'react';
import {
  Link2, Check, AlertCircle, Loader2, ExternalLink,
  MessageSquare, Github, FileText, Bell, Database,
  GitBranch, Cloud, Settings, ChevronRight, X,
  Zap, RefreshCw, Key, Eye, EyeOff, Shield, ArrowRight,
  Info, Copy, HelpCircle, Plus, GitPullRequest, CircleDot,
  Lock, Layers
} from 'lucide-react';
import api from '@/api';
import VedayaConnect from '@/VedayaConnect';
import { LinearLogo, HarnessLogo, CriblLogo, BuildkiteLogo, DatadogLogo, PagerDutyLogo } from './logos';

// Jira Logo SVG
function JiraLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="jira-gradient-1" x1="99.68%" y1="15.71%" x2="24.44%" y2="64.27%">
          <stop offset="18%" stopColor="#0052CC" />
          <stop offset="100%" stopColor="#2684FF" />
        </linearGradient>
        <linearGradient id="jira-gradient-2" x1="0.59%" y1="84.87%" x2="75.74%" y2="35.73%">
          <stop offset="18%" stopColor="#0052CC" />
          <stop offset="100%" stopColor="#2684FF" />
        </linearGradient>
      </defs>
      <path d="M11.53 2c0 2.4 1.97 4.35 4.4 4.35h.26v.25c0 2.4 1.97 4.35 4.4 4.35V2.73A.73.73 0 0019.87 2H11.53z" fill="url(#jira-gradient-1)" />
      <path d="M7.33 6.35c0 2.4 1.97 4.35 4.4 4.35h.26v.25c0 2.4 1.97 4.35 4.4 4.35V7.08a.73.73 0 00-.73-.73H7.33z" fill="url(#jira-gradient-2)" />
      <path d="M3.13 10.7c0 2.4 1.97 4.35 4.4 4.35h.26v.25c0 2.4 1.97 4.35 4.4 4.35v-8.22a.73.73 0 00-.73-.73H3.13z" fill="#2684FF" />
    </svg>
  );
}

// Slack logo SVG
function SlackLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 127 127" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z" fill="#E01E5A" />
      <path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z" fill="#36C5F0" />
      <path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z" fill="#2EB67D" />
      <path d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z" fill="#ECB22E" />
    </svg>
  );
}

// GitHub Logo SVG
function GitHubLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

// GitLab Logo SVG
function GitLabLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.45.044 13.587a.924.924 0 00.331 1.023L12 23.054l11.625-8.443a.92.92 0 00.33-1.024" fill="#FC6D26" />
      <path d="M12 23.054L16.418 9.45H7.582L12 23.054z" fill="#E24329" />
      <path d="M12 23.054l-4.418-13.6H1.387L12 23.053z" fill="#FC6D26" />
      <path d="M1.387 9.452L.045 13.587a.924.924 0 00.331 1.023L12 23.054 1.387 9.452z" fill="#FCA326" />
      <path d="M1.387 9.452h6.195L4.918 1.263a.455.455 0 00-.867 0L1.387 9.452z" fill="#E24329" />
      <path d="M12 23.054l4.418-13.6h6.195L12 23.053z" fill="#FC6D26" />
      <path d="M22.613 9.452l1.342 4.135a.924.924 0 01-.331 1.023L12 23.054l10.613-13.602z" fill="#FCA326" />
      <path d="M22.613 9.452h-6.195l2.664-8.189a.455.455 0 01.867 0l2.664 8.189z" fill="#E24329" />
    </svg>
  );
}

// Copy button
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="text-primary hover:text-primary/80 text-sm font-medium">
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// Slack Setup Modal
function SlackSetupModal({ onClose, onSuccess }) {
  const [connectionName, setConnectionName] = useState('Slack Connection');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!clientId || !clientSecret || !signingSecret || !redirectUrl) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate redirect URL format
    if (!redirectUrl.startsWith('https://') || !redirectUrl.includes('/api/connect/callback/slack')) {
      setError('Redirect URL must start with https:// and end with /api/connect/callback/slack');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.post('/slack/credentials', {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        signing_secret: signingSecret.trim(),
        redirect_uri: redirectUrl.trim()
      });

      // Use the new connect session flow
      const response = await api.post('/connect/session', {
        provider: 'slack',
        return_to: window.location.pathname
      });

      const { connect_url } = response.data;

      // Open OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        connect_url,
        'slack_oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      if (!popup) {
        window.location.href = connect_url;
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Failed to save credentials:', err);
      setError(err.response?.data?.detail || 'Failed to save credentials');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <SlackLogo className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create Slack Connection</h2>
              <p className="text-sm text-gray-500">Connect your Slack to sync and search its content</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Connection Name */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </label>
            <input
              type="text"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="My Slack Workspace"
            />
          </div>

          {/* OAuth Credentials Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                OAuth Credentials
              </label>
              <a
                href="https://api.slack.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-info hover:text-info flex items-center gap-1"
              >
                Get credentials
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 space-y-4">
              {/* Client ID */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Client ID <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono bg-white"
                  placeholder="1234567890.1234567890123"
                />
              </div>

              {/* Client Secret */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Client Secret <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showSecrets ? "text" : "password"}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono bg-white"
                    placeholder="••••••••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecrets(!showSecrets)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Signing Secret */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Signing Secret <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showSecrets ? "text" : "password"}
                    value={signingSecret}
                    onChange={(e) => setSigningSecret(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono bg-white"
                    placeholder="••••••••••••••••"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500 flex items-start gap-1.5">
                <HelpCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Find these in your Slack App's "Basic Information" page under "App Credentials"
              </p>
            </div>
          </div>

          {/* Redirect URL */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Redirect URL <span className="text-destructive">*</span>
              </label>
              {redirectUrl && <CopyButton text={redirectUrl} />}
            </div>
            <input
              type="text"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono bg-white"
              placeholder="https://vedaya-api.gauranshtandon.workers.dev/api/connect/callback/slack"
            />
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium text-warning">How to get your Redirect URL:</p>
              <ol className="text-xs text-warning list-decimal list-inside space-y-1">
                <li>Copy the Workers URL (e.g., <code className="bg-warning/20 px-1 rounded">https://vedaya-api.gauranshtandon.workers.dev</code>)</li>
                <li>Add <code className="bg-warning/20 px-1 rounded">/api/connect/callback/slack</code> to the end</li>
              </ol>
              <p className="text-xs text-warning mt-1">
                Add this same URL to "OAuth & Permissions" → "Redirect URLs" in your Slack app
              </p>
            </div>
          </div>

          {/* Required Scopes Info */}
          <div className="bg-info/10 rounded-xl p-4">
            <h4 className="text-sm font-medium text-info mb-2">Required Bot Token Scopes</h4>
            <div className="flex flex-wrap gap-1.5">
              {['channels:read', 'channels:history', 'groups:read', 'groups:history', 'users:read', 'chat:write'].map(scope => (
                <span key={scope} className="px-2 py-0.5 bg-info/10 text-info text-xs font-mono rounded">
                  {scope}
                </span>
              ))}
            </div>
            <p className="text-xs text-info mt-2">
              Add these scopes in "OAuth & Permissions" → "Scopes" → "Bot Token Scopes"
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-gray-700 hover:bg-gray-200 font-medium rounded-lg"
          >
            Back
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !clientId || !clientSecret || !signingSecret || !redirectUrl}
            className="px-6 py-2.5 bg-info hover:bg-info/90 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-medium rounded-lg flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Create'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Integration card for placeholder integrations
function IntegrationCard({ name, description, icon: Icon, color = 'blue' }) {
  const colorClasses = {
    purple: 'bg-primary/10 text-primary',
    blue: 'bg-info/10 text-info',
    green: 'bg-success/10 text-success',
    orange: 'bg-warning/10 text-warning',
    gray: 'bg-secondary text-muted-foreground',
  };

  return (
    <div className="card p-5 hover:border-primary/30 transition-colors">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2.5 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-medium text-foreground">{name}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <button
        disabled
        className="w-full px-4 py-2 bg-secondary text-muted-foreground text-sm font-medium rounded-lg cursor-not-allowed"
      >
        Coming Soon
      </button>
    </div>
  );
}

// Format time ago
function formatTimeAgo(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function IntegrationsHub({ onNavigateToSync, autoOpenConnect, onAutoOpenHandled }) {
  const [slackStatus, setSlackStatus] = useState(null);
  const [githubStatus, setGithubStatus] = useState(null);
  const [jiraStatus, setJiraStatus] = useState(null);
  const [gitlabStatus, setGitlabStatus] = useState(null);
  const [linearStatus, setLinearStatus] = useState(null);
  const [harnessStatus, setHarnessStatus] = useState(null);
  const [criblStatus, setCriblStatus] = useState(null);
  const [buildkiteStatus, setBuildkiteStatus] = useState(null);
  const [datadogStatus, setDatadogStatus] = useState(null);
  const [pagerdutyStatus, setPagerdutyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSlackSetupModal, setShowSlackSetupModal] = useState(false);
  const [showVedayaConnect, setShowVedayaConnect] = useState(false);
  const [connectMode, setConnectMode] = useState('connect');  // 'connect' or 'manage'
  const [connectProvider, setConnectProvider] = useState(null);  // 'github', 'slack', 'jira', 'gitlab', 'linear', 'harness', etc.

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const [slackRes, githubRes, jiraRes, gitlabRes, linearRes, harnessRes, criblRes, buildkiteRes, datadogRes, pagerdutyRes] = await Promise.all([
          api.get('/slack/status').catch(() => ({ data: { configured: false, status: 'not_configured' } })),
          api.get('/github/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/jira/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/gitlab/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/linear/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/harness/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/cribl/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/buildkite/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/datadog/status').catch(() => ({ data: { connected: false, status: 'not_configured' } })),
          api.get('/pagerduty/status').catch(() => ({ data: { connected: false, status: 'not_configured' } }))
        ]);
        setSlackStatus(slackRes.data);
        setGithubStatus(githubRes.data);
        setJiraStatus(jiraRes.data);
        setGitlabStatus(gitlabRes.data);
        setLinearStatus(linearRes.data);
        setHarnessStatus(harnessRes.data);
        setCriblStatus(criblRes.data);
        setBuildkiteStatus(buildkiteRes.data);
        setDatadogStatus(datadogRes.data);
        setPagerdutyStatus(pagerdutyRes.data);
      } catch (err) {
        console.error('Failed to fetch status:', err);
        setSlackStatus({ configured: false, status: 'not_configured' });
        setGithubStatus({ connected: false, status: 'not_configured' });
        setJiraStatus({ connected: false, status: 'not_configured' });
        setGitlabStatus({ connected: false, status: 'not_configured' });
        setLinearStatus({ connected: false, status: 'not_configured' });
        setHarnessStatus({ connected: false, status: 'not_configured' });
        setCriblStatus({ connected: false, status: 'not_configured' });
        setBuildkiteStatus({ connected: false, status: 'not_configured' });
        setDatadogStatus({ connected: false, status: 'not_configured' });
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();

    // Listen for OAuth popup completion
    const handleOAuthMessage = async (event) => {
      if (event.data?.type === 'oauth_complete') {
        console.log('OAuth complete:', event.data);
        // Refresh status after OAuth completes
        await fetchStatus();
      }
    };
    window.addEventListener('message', handleOAuthMessage);

    // Check for OAuth callbacks
    const params = new URLSearchParams(window.location.search);
    if (params.get('github_connected') === 'true') {
      setShowVedayaConnect(true);
    }

    return () => window.removeEventListener('message', handleOAuthMessage);
  }, []);

  // Auto-open connect modal when navigating from DataSync with a provider
  useEffect(() => {
    if (autoOpenConnect) {
      setShowVedayaConnect(true);
      onAutoOpenHandled?.();
    }
  }, [autoOpenConnect, onAutoOpenHandled]);

  const handleSlackConnect = async () => {
    try {
      // Use the same flow as VedayaConnect - create session and open popup
      const response = await api.post('/connect/session', {
        provider: 'slack',
        return_to: window.location.pathname
      });

      const { connect_url } = response.data;

      // Open OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        connect_url,
        'slack_oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      if (!popup) {
        // Popup blocked - redirect instead
        window.location.href = connect_url;
      }
    } catch (err) {
      console.error('Failed to connect Slack:', err);
    }
  };

  const handleSlackDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Slack?')) return;
    try {
      await api.delete('/slack/disconnect');
      const response = await api.get('/slack/status');
      setSlackStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect Slack:', err);
    }
  };

  const handleGithubDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect GitHub? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/github/disconnect');
      console.log('GitHub disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/github/status');
      setGithubStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect GitHub:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect GitHub';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshGithubStatus = async () => {
    try {
      const response = await api.get('/github/status');
      setGithubStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh GitHub status:', err);
    }
  };

  const handleJiraDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Jira? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/jira/disconnect');
      console.log('Jira disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/jira/status');
      setJiraStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect Jira:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect Jira';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshJiraStatus = async () => {
    try {
      const response = await api.get('/jira/status');
      setJiraStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh Jira status:', err);
    }
  };

  const handleGitlabDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect GitLab? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/gitlab/disconnect');
      console.log('GitLab disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/gitlab/status');
      setGitlabStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect GitLab:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect GitLab';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLinearDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Linear? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/linear/disconnect');
      console.log('Linear disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/linear/status');
      setLinearStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect Linear:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect Linear';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshGitlabStatus = async () => {
    try {
      const response = await api.get('/gitlab/status');
      setGitlabStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh GitLab status:', err);
    }
  };

  const refreshLinearStatus = async () => {
    try {
      const response = await api.get('/linear/status');
      setLinearStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh Linear status:', err);
    }
  };

  const handleHarnessDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Harness? This will remove all synced pipeline data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/harness/disconnect');
      console.log('Harness disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/harness/status');
      setHarnessStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect Harness:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect Harness';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshHarnessStatus = async () => {
    try {
      const response = await api.get('/harness/status');
      setHarnessStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh Harness status:', err);
    }
  };

  const handleCriblDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Cribl? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/cribl/disconnect');
      console.log('Cribl disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/cribl/status');
      setCriblStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect Cribl:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect Cribl';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshCriblStatus = async () => {
    try {
      const response = await api.get('/cribl/status');
      setCriblStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh Cribl status:', err);
    }
  };

  const handleBuildkiteDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Buildkite? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/buildkite/disconnect');
      console.log('Buildkite disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/buildkite/status');
      setBuildkiteStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect Buildkite:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect Buildkite';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshBuildkiteStatus = async () => {
    try {
      const response = await api.get('/buildkite/status');
      setBuildkiteStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh Buildkite status:', err);
    }
  };

  const handleDatadogDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Datadog? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/datadog/disconnect');
      console.log('Datadog disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/datadog/status');
      setDatadogStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect Datadog:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect Datadog';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshDatadogStatus = async () => {
    try {
      const response = await api.get('/datadog/status');
      setDatadogStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh Datadog status:', err);
    }
  };

  const handlePagerDutyDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect PagerDuty? This will remove all synced data.')) return;
    try {
      setLoading(true);
      const result = await api.delete('/pagerduty/disconnect');
      console.log('PagerDuty disconnect result:', result.data);

      // Refresh status after disconnect
      const response = await api.get('/pagerduty/status');
      setPagerdutyStatus(response.data);
    } catch (err) {
      console.error('Failed to disconnect PagerDuty:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to disconnect PagerDuty';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshPagerDutyStatus = async () => {
    try {
      const response = await api.get('/pagerduty/status');
      setPagerdutyStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh PagerDuty status:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const slackConnected = slackStatus?.status === 'connected' || slackStatus?.workspace?.team_id;
  const slackCredentialsConfigured = slackStatus?.oauth_available || slackStatus?.credentials_configured;
  const githubConnected = githubStatus?.connected;
  const jiraConnected = jiraStatus?.connected;
  const gitlabConnected = gitlabStatus?.connected;
  const linearConnected = linearStatus?.connected;
  const harnessConnected = harnessStatus?.connected;
  const criblConnected = criblStatus?.connected;
  const buildkiteConnected = buildkiteStatus?.connected;
  const datadogConnected = datadogStatus?.connected;
  const pagerdutyConnected = pagerdutyStatus?.connected;

  return (
    <div className="space-y-6">
      {/* Modals */}
      {showSlackSetupModal && (
        <SlackSetupModal
          onClose={() => setShowSlackSetupModal(false)}
          onSuccess={() => setShowSlackSetupModal(false)}
        />
      )}

      <VedayaConnect
        isOpen={showVedayaConnect}
        onClose={() => {
          setShowVedayaConnect(false);
          setConnectMode('connect');
          setConnectProvider(null);
          refreshGithubStatus();
          refreshJiraStatus();
          refreshGitlabStatus();
          refreshLinearStatus();
          refreshHarnessStatus();
          refreshCriblStatus();
          refreshBuildkiteStatus();
          refreshDatadogStatus();
        }}
        onSuccess={() => {
          refreshGithubStatus();
          refreshJiraStatus();
          refreshGitlabStatus();
          refreshLinearStatus();
          refreshHarnessStatus();
          refreshCriblStatus();
          refreshBuildkiteStatus();
          refreshDatadogStatus();
          setConnectMode('connect');
          setConnectProvider(null);
          if (onNavigateToSync) onNavigateToSync();
        }}
        initialProvider={connectProvider || autoOpenConnect}
        mode={connectMode}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Integrations</h2>
          <p className="text-muted-foreground mt-1">Connect your tools to enhance incident investigation</p>
        </div>
        <button
          onClick={() => setShowVedayaConnect(true)}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4" />
          Connect Tool
        </button>
      </div>

      {/* Connected Summary */}
      {(slackConnected || githubConnected || jiraConnected || gitlabConnected || linearConnected || harnessConnected || criblConnected || buildkiteConnected || datadogConnected || pagerdutyConnected) && (
        <div className="card-glow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <Check className="w-5 h-5 text-success" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-foreground">
                {[slackConnected && 'Slack', githubConnected && 'GitHub', gitlabConnected && 'GitLab', jiraConnected && 'Jira', linearConnected && 'Linear', harnessConnected && 'Harness', criblConnected && 'Cribl', buildkiteConnected && 'Buildkite', datadogConnected && 'Datadog', pagerdutyConnected && 'PagerDuty'].filter(Boolean).join(' & ')} Connected
              </h3>
              <p className="text-sm text-muted-foreground">
                Your connected tools are syncing data for investigation
              </p>
            </div>
            {onNavigateToSync && (
              <button
                onClick={onNavigateToSync}
                className="btn btn-primary text-sm"
              >
                View Data Sync
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* GitHub Integration */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Source Control
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* GitHub Card */}
          <div className={`bg-white rounded-xl border-2 ${githubConnected ? 'border-success/30' : 'border-gray-200 hover:border-gray-300'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-gray-900 text-white">
                <GitHubLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">GitHub</h3>
                  {githubConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Sync repos, PRs, and issues</p>
              </div>
            </div>

            {githubConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    {githubStatus?.account?.avatar_url && (
                      <img src={githubStatus.account.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                    )}
                    <span className="text-gray-600">Connected as </span>
                    <span className="font-medium text-gray-900">@{githubStatus?.account?.login}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{githubStatus?.repos_selected_count || 0} repos</span>
                    <span>{githubStatus?.metrics?.prs_ingested || 0} PRs</span>
                    <span>{githubStatus?.metrics?.issues_ingested || 0} issues</span>
                    {githubStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{githubStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('github');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Repos
                  </button>
                  <button
                    onClick={handleGithubDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowVedayaConnect(true)}
                className="w-full px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <GitHubLogo className="w-4 h-4" />
                Connect GitHub
              </button>
            )}
          </div>

          {/* GitLab Card */}
          <div className={`bg-white rounded-xl border-2 ${gitlabConnected ? 'border-success/30' : 'border-gray-200 hover:border-warning/40'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-warning/10">
                <GitLabLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">GitLab</h3>
                  {gitlabConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Sync projects, MRs, and pipelines</p>
              </div>
            </div>

            {gitlabConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    {gitlabStatus?.account?.avatar_url && (
                      <img src={gitlabStatus.account.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                    )}
                    <span className="text-gray-600">Connected as </span>
                    <span className="font-medium text-gray-900">@{gitlabStatus?.account?.username}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{gitlabStatus?.projects_selected_count || 0} projects</span>
                    <span>{gitlabStatus?.metrics?.mrs_ingested || 0} MRs</span>
                    <span>{gitlabStatus?.metrics?.pipelines_ingested || 0} pipelines</span>
                    {gitlabStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{gitlabStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('gitlab');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Projects
                  </button>
                  <button
                    onClick={handleGitlabDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('gitlab');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-warning hover:bg-warning/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <GitLabLogo className="w-4 h-4" />
                Connect GitLab
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Slack Integration */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Communication
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Slack Card */}
          <div className={`bg-white rounded-xl border-2 ${slackConnected ? 'border-success/30' : 'border-gray-200 hover:border-primary/40'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <SlackLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">Slack</h3>
                  {slackConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Sync incident threads from your workspace</p>
              </div>
            </div>

            {slackConnected ? (
              <div className="space-y-3">
                {/* Connection Info - like GitHub */}
                <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <SlackLogo className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">{slackStatus?.workspace?.team_name || 'Workspace'}</span>
                  </div>
                </div>

                {/* Stats row - like GitHub's repos/PRs/issues */}
                <div className="flex items-center gap-4 text-xs text-gray-500 px-1">
                  <span>{slackStatus?.monitored_channels?.length || 0} channels</span>
                  <span>{slackStatus?.metrics?.threads_ingested || 0} threads</span>
                  {slackStatus?.metrics?.vedaya_indexed > 0 && (
                    <span className="text-info">{slackStatus?.metrics?.vedaya_indexed} indexed</span>
                  )}
                </div>

                {/* Actions - match GitHub layout */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectProvider('slack');
                      setConnectMode('manage');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Channels
                  </button>
                  <button
                    onClick={handleSlackDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : slackCredentialsConfigured ? (
              <button
                onClick={handleSlackConnect}
                className="w-full px-4 py-2.5 bg-[#4A154B] hover:bg-[#3a1039] text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <SlackLogo className="w-4 h-4" />
                Connect to Slack
              </button>
            ) : (
              <button
                onClick={() => setShowSlackSetupModal(true)}
                className="w-full px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Set Up Connection
              </button>
            )}
          </div>

          {/* Microsoft Teams */}
          <IntegrationCard
            name="Microsoft Teams"
            description="Import incident channels from Teams"
            icon={MessageSquare}
            color="blue"
          />
        </div>
      </div>

      {/* Issue Tracking */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Issue Tracking
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Jira Card */}
          <div className={`bg-white rounded-xl border-2 ${jiraConnected ? 'border-success/30' : 'border-gray-200 hover:border-gray-300'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-info/10">
                <JiraLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">Jira</h3>
                  {jiraConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Sync issues, epics, and sprints</p>
              </div>
            </div>

            {jiraConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    {jiraStatus?.account?.avatar_url && (
                      <img src={jiraStatus.account.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                    )}
                    <span className="text-gray-600">Connected to </span>
                    <span className="font-medium text-gray-900">{jiraStatus?.account?.display_name || jiraStatus?.account?.site_url}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{jiraStatus?.projects_selected_count || 0} projects</span>
                    <span>{jiraStatus?.metrics?.issues_ingested || 0} issues</span>
                    <span>{jiraStatus?.metrics?.epics_ingested || 0} epics</span>
                    {jiraStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{jiraStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('jira');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Projects
                  </button>
                  <button
                    onClick={handleJiraDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('jira');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-info hover:bg-info/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <JiraLogo className="w-4 h-4" />
                Connect Jira
              </button>
            )}
          </div>

          {/* Linear Card */}
          <div className={`bg-white rounded-xl border-2 ${linearConnected ? 'border-success/30' : 'border-gray-200 hover:border-gray-300'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <LinearLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">Linear</h3>
                  {linearConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Sync issues, cycles, and projects</p>
              </div>
            </div>

            {linearConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    {linearStatus?.account?.avatar_url && (
                      <img src={linearStatus.account.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                    )}
                    <span className="text-gray-600">Connected to </span>
                    <span className="font-medium text-gray-900">{linearStatus?.account?.organization_name || linearStatus?.account?.user_name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{linearStatus?.teams_selected_count || 0} teams</span>
                    <span>{linearStatus?.metrics?.issues_ingested || 0} issues</span>
                    <span>{linearStatus?.metrics?.cycles_ingested || 0} cycles</span>
                    {linearStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{linearStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('linear');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Teams
                  </button>
                  <button
                    onClick={handleLinearDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('linear');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <LinearLogo className="w-4 h-4" />
                Connect Linear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Data Observability */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Data Observability
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Cribl Card */}
          <div className={`bg-white rounded-xl border-2 ${criblConnected ? 'border-success/30' : 'border-gray-200 hover:border-info/40'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-info/10">
                <CriblLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">Cribl</h3>
                  {criblConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Pipelines, sources & data routing</p>
              </div>
            </div>

            {criblConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Connected to </span>
                    <span className="font-medium text-gray-900">{criblStatus?.account?.organization_name || 'Cribl Cloud'}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{criblStatus?.worker_groups_selected_count || 0} worker groups</span>
                    <span>{criblStatus?.metrics?.pipelines_ingested || 0} pipelines</span>
                    <span>{criblStatus?.fleet_count || 0} fleets</span>
                    {criblStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{criblStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('cribl');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Groups
                  </button>
                  <button
                    onClick={handleCriblDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('cribl');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-info hover:bg-info/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <CriblLogo className="w-4 h-4" />
                Connect Cribl
              </button>
            )}
          </div>

          {/* Splunk placeholder */}
          <IntegrationCard name="Splunk" description="Log aggregation & search" icon={Database} color="green" />
        </div>
      </div>

      {/* CI/CD */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          CI/CD
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Harness Card */}
          <div className={`bg-white rounded-xl border-2 ${harnessConnected ? 'border-success/30' : 'border-gray-200 hover:border-info/40'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-info/10">
                <HarnessLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">Harness</h3>
                  {harnessConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Sync pipelines and deployments</p>
              </div>
            </div>

            {harnessConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Connected to </span>
                    <span className="font-medium text-gray-900">{harnessStatus?.account?.account_name || harnessStatus?.account?.account_identifier || 'Harness'}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{harnessStatus?.pipelines_selected_count || 0} pipelines</span>
                    <span>{harnessStatus?.metrics?.executions_ingested || 0} executions</span>
                    {harnessStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{harnessStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('harness');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Pipelines
                  </button>
                  <button
                    onClick={handleHarnessDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('harness');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-info hover:bg-info/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <HarnessLogo className="w-4 h-4" />
                Connect Harness
              </button>
            )}
          </div>

          {/* Buildkite Card */}
          <div className={`bg-white rounded-xl border-2 ${buildkiteConnected ? 'border-success/30' : 'border-gray-200 hover:border-success/40'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-success/10">
                <BuildkiteLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">Buildkite</h3>
                  {buildkiteConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Pipelines, builds & agents</p>
              </div>
            </div>

            {buildkiteConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Connected to </span>
                    <span className="font-medium text-gray-900">{buildkiteStatus?.account?.organization_name || buildkiteStatus?.account?.organization_slug}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{buildkiteStatus?.pipelines_selected_count || 0} pipelines</span>
                    <span>{buildkiteStatus?.metrics?.builds_ingested || 0} builds</span>
                    <span>{buildkiteStatus?.agent_count || 0} agents</span>
                    {buildkiteStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{buildkiteStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('buildkite');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage Pipelines
                  </button>
                  <button
                    onClick={handleBuildkiteDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('buildkite');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-success hover:bg-success/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <BuildkiteLogo className="w-4 h-4" />
                Connect Buildkite
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Alerting & Monitoring
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* PagerDuty Card */}
          <div className={`bg-white rounded-xl border-2 ${pagerdutyConnected ? 'border-success/30' : 'border-gray-200 hover:border-success/40'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-success/10">
                <PagerDutyLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">PagerDuty</h3>
                  {pagerdutyConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Import on-call schedules & incidents</p>
              </div>
            </div>

            {pagerdutyConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Connected to </span>
                    <span className="font-medium text-gray-900">{pagerdutyStatus?.account?.account_name || pagerdutyStatus?.account?.subdomain || 'PagerDuty'}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{pagerdutyStatus?.service_count || 0} services</span>
                    <span>{pagerdutyStatus?.schedule_count || 0} schedules</span>
                    <span>{pagerdutyStatus?.incident_count || 0} incidents</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('pagerduty');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage
                  </button>
                  <button
                    onClick={handlePagerDutyDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('pagerduty');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-success hover:bg-success/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <PagerDutyLogo className="w-4 h-4" />
                Connect PagerDuty
              </button>
            )}
          </div>

          {/* Datadog Card */}
          <div className={`bg-white rounded-xl border-2 ${datadogConnected ? 'border-success/30' : 'border-gray-200 hover:border-primary/40'} p-5 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <DatadogLogo className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">Datadog</h3>
                  {datadogConnected && (
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Correlate metrics with incidents</p>
              </div>
            </div>

            {datadogConnected ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Connected to </span>
                    <span className="font-medium text-gray-900">{datadogStatus?.account?.organization_name || datadogStatus?.account?.site || 'Datadog'}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{datadogStatus?.dashboards_count || 0} dashboards</span>
                    <span>{datadogStatus?.metrics?.monitors_ingested || 0} monitors</span>
                    <span>{datadogStatus?.metrics?.alerts_ingested || 0} alerts</span>
                    {datadogStatus?.metrics?.vedaya_indexed > 0 && (
                      <span className="text-info">{datadogStatus?.metrics?.vedaya_indexed} indexed</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConnectMode('manage');
                      setConnectProvider('datadog');
                      setShowVedayaConnect(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Manage
                  </button>
                  <button
                    onClick={handleDatadogDisconnect}
                    className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-white text-sm font-medium rounded-lg"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setConnectProvider('datadog');
                  setShowVedayaConnect(true);
                }}
                className="w-full px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <DatadogLogo className="w-4 h-4" />
                Connect Datadog
              </button>
            )}
          </div>

          <IntegrationCard name="Grafana" description="Link dashboards to investigations" icon={Cloud} color="orange" />
        </div>
      </div>
    </div>
  );
}
